import type { OddsMessage } from '../type';
import type {
  MatchData,
  OddsData,
  OddsRecommendData,
  LeftSportCountData,
  TopSportCountData,
} from '../proto/notification';
import { createSharedRoot } from '../../../request/src/globalStore';
import { EventEnum, BalanceChangeData } from '../type';
import useSocket from './useSocket';

/**
 * WebSocket 业务层（wsd）职责概括：
 * 1. **订阅**：`sendSubscriptionMsg` / `sendUnsubscriptionMsg` 通过底层 `socket.send` 发 `{ type: 'subscribe'|'unsubscribe', topics: [...] }`；
 *    与 `~/primitives/useSubscribe` 配合：登录后把若干 topic（如用户 ID、`BZ:VIP:userId`）合并进数组并带去重、重连后重新 subscribe。
 * 2. **下行**：`useSocket` 收包 → protobuf/json 解成 `MessageContent` → {@link generateWSData} 转为 `BusinessMessage[]` → `onEvent` 用 lodash `filter` 按 `type` 等字段分发给各 `useXxx` hook。
 * 3. **重连**：`useOnReconnect` 会 `counter.clear()`，需由业务再次调用 `sendSubscriptionMsg` 恢复订阅（`useSubscribe` 已处理）。
 */
const useSocketBusiness = createSharedRoot(() => {
  /** 对 topics 的引用计数 */
  const counter = new Map<string | number, number>();

  /** socket 实例 */
  const socket = useSocket();

  /**
   * 订阅消息
   * @param topics
   * @returns
   */
  const sendSubscriptionMsg = (topics: (number | string)[]) => {
    /** 增加引用计数 */
    const target: (number | string)[] = [];
    topics.forEach((topic) => {
      const count = (counter.get(topic) || 0) + 1;
      count === 1 && target.push(topic);
      counter.set(topic, count);
    });

    if (target.length === 0) return;

    socket.send({ type: 'subscribe', topics: target });
  };

  /**
   * 取消订阅消息
   * @param topics
   * @returns
   */
  const sendUnsubscriptionMsg = (topics: (number | string)[]) => {
    /** 减少引用计数 */
    const target: (number | string)[] = [];
    topics.forEach((topic) => {
      const count = (counter.get(topic) || 0) - 1;
      if (count <= 0) {
        counter.delete(topic);
        target.push(topic);
      } else {
        counter.set(topic, count);
      }
    });

    if (target.length === 0) return;

    socket.send({ type: 'unsubscribe', topics: target });
  };

  /**
   * socket 重连
   * @param cb
   */
  const useOnReconnect = (cb: (retryCount?: number) => void) => {
    socket.onReconnect((count) => {
      counter.clear();
      cb(count);
    });
  };

  /**
   * 比赛状态、时间、比分等数据推送
   * @param matchId
   * @returns
   */
  const useMatchPush = (matchId: Accessor<string>) => {
    const [data, setData] = createSignal<MatchData>();

    createEffect(
      on(matchId, (id) => {
        const dispose = socket.onEvent({ type: EventEnum.match, matchId: id }, (data) => {
          if (data.type === EventEnum.match) {
            setData(data.data);
          }
        });

        onCleanup(dispose);
      })
    );

    return data;
  };

  /**
   * 赔率数据推送
   * @param code
   * @returns
   */
  const useOddWS = (code: Accessor<string>) => {
    const [data, setData] = createSignal<OddsData>();

    createEffect(
      on(code, (code) => {
        const dispose = socket.onEvent({ type: EventEnum.odds, code: String(code) }, (data) => {
          if (data.type === EventEnum.odds) {
            setData(data.data);
          }
        });

        onCleanup(dispose);
      })
    );

    return data;
  };

  /**
   * 赔率推荐盘数据变更推送
   * @returns
   */
  const useOddsRecommend = () => {
    const [data, setData] = createSignal<OddsRecommendData>();

    const dispose = socket.onEvent({ type: EventEnum.odds_recommend }, (data) => {
      if (data.type === EventEnum.odds_recommend) {
        setData(data.data);
      }
    });

    onCleanup(dispose);

    return data;
  };

  /**
   * 滚球盘开盘通知
   * @returns
   */
  const useOddsHandover = (matchId: string) => {
    const [data, setData] = createSignal(0);
    const dispose = socket.onEvent({ type: EventEnum.odds_handover, matchId: matchId }, (data) => {
      if (data.type === EventEnum.odds_handover) {
        setData((count) => count + 1);
      }
    });

    onCleanup(dispose);

    return data;
  };

  /**
   * 盘口数据推送
   * @param code
   * @param matchId
   * @returns
   */
  const useMarketDataWS = (params: Accessor<Partial<Omit<OddsMessage, 'type'>>>) => {
    const [data, setData] = createSignal<OddsData>();

    createEffect(
      on(params, (evtParams) => {
        const dispose = socket.onEvent({ type: EventEnum.odds, ...evtParams }, (data) => {
          if (data.type === EventEnum.odds) {
            setData(data?.data);
          }
        });

        onCleanup(dispose);
      })
    );

    return data;
  };

  /**
   * live/upcoming赛事比赛数量推送
   * @returns
   */
  const useTopSportCountPush = () => {
    const [data, setData] = createSignal<TopSportCountData>();

    const dispose = socket.onEvent({ type: EventEnum.top_sport_count }, (data) => {
      if (data.type === EventEnum.top_sport_count) {
        setData(data.data);
      }
    });

    onCleanup(dispose);

    return data;
  };

  /**
   * 侧边栏运动比赛数量推送
   * @returns
   */
  const useLeftSportCountPush = () => {
    const [data, setData] = createSignal<LeftSportCountData>();

    const dispose = socket.onEvent({ type: EventEnum.left_sport_count }, (data) => {
      if (data.type === EventEnum.left_sport_count) {
        setData(data.data);
      }
    });

    onCleanup(dispose);

    return data;
  };

  /**
   * 站内信消息推送
   * @returns
   */
  const useInSiteMessage = () => {
    const [data, setData] = createSignal<Record<string, any> & { data?: { event?: string } }>();

    const dispose = socket.onEvent({ type: EventEnum.notification_msg_route }, (data: any) => {
      if (data.type === EventEnum.notification_msg_route) {
        let content = data?.data;
        try {
          content = typeof data.data === 'string' ? JSON.parse(data.data) : data.data;
        } catch (error) {
          console.error('解析站内信消息失败', error);
        }
        setData({
          type: data.type,
          timestamp: Date.now(),
          data: content,
        });
      }
    });

    onCleanup(dispose);

    return data;
  };

  /**
   * 余额变更推送
   * @param cb
   */
  const useBalanceChangePush = (cb: (data: BalanceChangeData) => void) => {
    const dispose = socket.onEvent({ type: EventEnum.balance_change_event }, (data: any) => {
      if (data.type === EventEnum.balance_change_event) {
        cb(data?.data?.data as unknown as BalanceChangeData);
      }
    });

    onCleanup(dispose);
  };

  /**
   * VIP 用户维度推送（需先订阅 topic `BZ:VIP:{userId}`）。
   */
  const useBzVipPush = () => {
    const [data, setData] = createSignal<Record<string, unknown> | undefined>();

    const dispose = socket.onEvent({ type: EventEnum.bz_vip }, (msg) => {
      if (msg.type === EventEnum.bz_vip) {
        setData(msg.data);
      }
    });

    onCleanup(dispose);

    return data;
  };

  return {
    /** 订阅消息 */
    sendSubscriptionMsg,
    /** 取消订阅消息 */
    sendUnsubscriptionMsg,
    /** socket 重连 */
    useOnReconnect,
    /** 比赛状态、时间、比分等数据推送 */
    useMatchPush,
    /** 赔率数据推送 */
    useOddWS,
    /** 赔率推荐盘数据变更推送 */
    useOddsRecommend,
    /** 盘口数据推送 */
    useMarketDataWS,
    /** 侧边栏运动比赛数量推送 */
    useLeftSportCountPush,
    /** live/upcoming赛事比赛数量推送 */
    useTopSportCountPush,
    /** 站内信消息推送 */
    useInSiteMessage,
    /** 余额变更推送 */
    useBalanceChangePush,
    /** 订阅 BZ:VIP:userId 后的 VIP 业务推送（type 需为 bz_vip） */
    useBzVipPush,
    /** 滚球盘开盘通知 */
    useOddsHandover,
  };
});

export default useSocketBusiness;
